const populatedValue = (value) =>
  value !== null && value !== undefined && value !== '' && value !== '-' && value !== 0;

const uniqueValues = (values) => [...new Set(values.filter(Boolean))];

export const attachDrilldownLineage = (row, sourceRows) => {
  const adjustmentRows = sourceRows.filter((sourceRow) => sourceRow.AdjustmentCode);
  row.DrilldownAdjustmentCodes = uniqueValues(
    adjustmentRows.map((sourceRow) => sourceRow.AdjustmentCode)
  );
  row.DrilldownAdjustmentTypes = uniqueValues(
    adjustmentRows.map((sourceRow) => sourceRow.AdjustmentSource || sourceRow.AdjustmentType)
  );
  row.DrilldownEntityCodes = uniqueValues(
    adjustmentRows.map((sourceRow) => sourceRow.EntityCode)
  );
  row.DrilldownGroupCodes = uniqueValues(
    adjustmentRows.map((sourceRow) => sourceRow.GroupCode)
  );
  row.DrilldownSubGroups = uniqueValues(
    adjustmentRows
      .filter((sourceRow) => sourceRow.SubGroupKey)
      .map((sourceRow) => `${sourceRow.SubGroupType || ''}::${sourceRow.SubGroupKey}`)
  );
};

// Attach the raw adjustment rows represented by each deferred section/grand total.
export const annotateDeferredDrilldowns = (rows) => {
  let sectionRows = [];
  let rowsSinceGrandTotal = [];

  rows.forEach((row) => {
    if (row.isSection) {
      sectionRows = [];
      return;
    }

    if (!row.isSubFooter && !row.isCalculatedTotal && row.AdjustmentCode) {
      sectionRows.push(row);
      rowsSinceGrandTotal.push(row);
      return;
    }

    if (!row.isSubFooter && !row.isCalculatedTotal) return;

    const isGrandTotal = row.AdjustmentName === 'GRAND TOTAL TEMPORARY DIFFERENCES';
    attachDrilldownLineage(row, isGrandTotal ? rowsSinceGrandTotal : sectionRows);

    if (isGrandTotal) rowsSinceGrandTotal = [];
  });

  return rows;
};

// Attach lineage to provision/ETR totals. Section footers use only the immediately
// preceding section; calculated totals use every raw adjustment row above them.
export const annotatePivotDrilldowns = (rows) => {
  let sectionRows = [];
  let cumulativeRows = [];

  rows.forEach((row) => {
    if (row.isSection) {
      sectionRows = [];
      return;
    }

    if (!row.isSubFooter && !row.isCalculatedTotal && row.AdjustmentCode) {
      sectionRows.push(row);
      cumulativeRows.push(row);
      return;
    }

    if (row.isSubFooter || row.isCalculatedTotal) {
      attachDrilldownLineage(row, row.isCalculatedTotal ? cumulativeRows : sectionRows);
    }
  });

  const allAdjustmentRows = rows.filter((row) => row.AdjustmentCode && !row.isSection);
  rows.forEach((row) => {
    if (
      (row.isSubFooter || row.isCalculatedTotal) &&
      (!row.DrilldownAdjustmentCodes || row.DrilldownAdjustmentCodes.length === 0)
    ) {
      attachDrilldownLineage(row, allAdjustmentRows);
    }
  });

  // Rate rows are driven by scenario/entity/state inputs, not adjustment inputs.
  rows.forEach((row) => {
    if (/RATE(?: PERCENTAGE)?$/i.test(row.AdjustmentName || '')) {
      row.DrilldownAdjustmentCodes = [];
    }
  });

  return rows;
};

export const getDeferredActivityTypes = (field, activityTypes) => {
  const baseField = String(field || '')
    .replace(/_TaxEffected$/, '')
    .replace(/_PostApportioned$/, '')
    .replace(/_FBOS$/, '');

  if (baseField === 'AdjBeginningBalance') {
    return ['BeginningBalance', 'ReturnToProvision', 'DeferredTrueUp', 'PurchaseAccounting'];
  }
  if (baseField === 'ReturnToProvisionRollforward') return ['ReturnToProvision'];
  if (baseField === 'DeferredTrueUpRollforward') return ['DeferredTrueUp'];
  if (baseField === 'CurrentActivity') return ['CurrentActivityPL'];
  if (baseField === 'WriteOff') {
    return ['DeferredExpAdjustment', 'CustomDeferredExpAdjustment'];
  }
  if (activityTypes.includes(baseField)) return [baseField];

  // These values are formulas over multiple activity columns. Showing every
  // underlying activity is the useful drill-down for their total cells.
  if (
    [
      'CTAAdjustment',
      'EndingBalance',
      'DeferredExpense',
      'TaxRateChange',
      'ValuationAllowance'
    ].includes(baseField)
  ) {
    return [...activityTypes];
  }

  return null;
};

export const canDrillIntoTotalCell = (params) =>
  Boolean(
    (params.row.isSubFooter || params.row.isCalculatedTotal) &&
    params.field !== 'AdjustmentName' &&
    populatedValue(params.value) &&
    params.row.DrilldownAdjustmentCodes?.length
  );
